// Rock Off Retail (22 Sep 2026): colour + side gallery filter, v2.
//
// v3 (23 Sep 2026): `collectGallerySlides` used to build the ONE filter list
// from the gallery slide `<li>`s only, then apply its hidden-id set to the
// thumbnails and modal by matching id - which silently skipped any element
// that had no counterpart in the gallery list. Before this build that
// happened for every OTHER variant's own bound media (Liquid's
// `hide_variants` skip kept them out of the gallery slides but never out of
// the modal - see product-media-gallery.liquid item 1's own comment), so
// those modal items were never evaluated and always showed regardless of
// the selected colour - the reported zoom defect. Liquid now renders every
// zone (gallery, thumbnails, modal) from the SAME media set for a Printed-
// option product, and this file now filters each zone from ITS OWN
// `data-ro-*` elements independently (never derives one zone's hidden set
// from another's), so a future desync between zones fails safe per zone
// rather than leaking one zone's gaps into another.
//
// PURPOSE: a product with several colours (Black, White, Charcoal, Grey,
// Acid Wash, ...) must show only the selected colour's mock-up photos, and
// a "Front Only" Printed selection must additionally hide the back photo
// for that colour. The 18 Sep patch only knew about "Front Only" (it
// skipped any slide whose alt contained "back view" in Liquid, with no
// idea of colour at all) - on a four-colour product every OTHER colour's
// mock-ups still showed. This file replaces that Liquid skip: every slide
// stays in the DOM (theme/snippets/product-media-gallery.liquid and
// product-media-modal.liquid stamp `data-ro-side`/`data-ro-colour` on each
// slide, parsed at render time from the bridge's own alt-text shape - see
// those files' own comments for the parsing rule) and this script decides,
// at runtime, which of them are visible - driven by data, never a
// hard-coded colour/garment list.
//
// Two halves:
//   1. `roFilterSlides(slides, { colour, printed })` - a pure function,
//      easy to unit test (see _sop/state/relb/item5/filter.test.js for v2's
//      coverage and _sop/state/gallery-v3/filter-v3.test.js for v3's),
//      taking already-parsed {id, side, colour} slide objects and the
//      CURRENTLY SELECTED Colour/Printed option values, returning the same
//      slides with a `visible` flag added. No DOM, no globals, no
//      Shopify-isms - safe to `require()` from Node.
//   2. DOM wiring (browser only, guarded so `require()`-ing this file in a
//      Node test never touches `window`/`document`) - reads the selected
//      Colour/Printed values from the product's option inputs (radio
//      swatches AND dropdowns, both carry `data-option-name` - see
//      theme/snippets/product-variant-options.liquid), reads the slide
//      data off EACH zone's own `[data-ro-*]` elements (gallery, thumbnail
//      list, modal - three independent reads, v3, see the comment above),
//      and reruns on load and on every PUB_SUB_EVENTS.variantChange.
//      Visibility uses `element.style.display`, which Dawn's own
//      slider-component already treats as "not a real slide" (`initPages()`
//      filters `sliderItems` down to ones with `clientWidth > 0`) - so
//      calling the slider's OWN `resetPages()` after hiding/showing slides
//      is enough to fix the counter and the prev/next arrows; this file
//      never recomputes slide counts itself.
//
// Products with no "Printed" option are never touched by the DOM half at
// all (see `hasPrintedOption` below) - Dawn's stock behaviour, no filter
// logic runs. The pure function mirrors this: pass `printed: null` (no
// Printed option) and every slide comes back visible, untouched, whatever
// its colour.
//
// Fixes after the live preview (23 Sep 2026, measured with Playwright on
// the duplicate theme, 164323524836):
//   1. The modal zone is now found by id (`findProductModalElement`), not
//      by ancestry - Dawn moves `<product-modal>` to be a direct child of
//      `<body>` at runtime, so the old `productInfoEl.querySelector(...)`
//      always returned null on the real page. Re-applied on the modal's
//      own open (`watchModalOpen`), not only on load/variant change.
//   2. `roFilterSlides` gained a `frontVariantKeys` rule so two
//      near-identical FRONT mock-ups bound to different Printed variants
//      of the same colour (e.g. Death Raven's "front view on Black M101"
//      vs "front view on Black M121") no longer both show for either
//      Printed choice - see the rule's own comment, below.
// (The left-rail flex-wrap fix for the same live-preview round is CSS
// only - theme/assets/section-main-product.css.)
//
// 26 Sep 2026 (Sohail): a model shot's alt carries no colour word at all
// ("Model view - M101 - 32") - only the GARMENT CODE the model is wearing
// (M101). Every model photo on a multi-colour product is the same model in
// the same garment, so on e.g. the Enslaved Angel T-Shirt they kept showing
// under Charcoal/Acid Wash even though the model is wearing Black. Fix:
// Liquid now also stamps `data-ro-garment-code` on every slide (parsed from
// the SAME alt text - see product-media-gallery.liquid/product-media-modal.
// liquid's own comment for exactly where the code comes from for a mock
// alt vs a model-shot alt), and `roFilterSlides` builds a garmentCode ->
// colour map from THIS PRODUCT's OWN mock slides (never a hard-coded
// garment/colour list) before filtering. A model shot's effective colour
// for the plain colour/side rule is its own `colour` if it has one
// (unchanged), else the map's colour for its `garmentCode`, else '' (no
// mock anywhere in this product carries that code - keeps today's
// behaviour: show for every colour, exactly as an unmapped colourless
// slide always has). No twin/alias table exists in this file, so a code
// with no direct mock match always falls through to "show" - there is
// nothing yet to fall back to.

// 29 Sep 2026 (Sohail): Colour-only products (a Colour option, NO Printed
// option) never reached the filter above (`hasPrintedOption` gate), so a
// model shot showed under every colour - a white-worn model under Black.
// Liquid now stamps `data-ro-model-colour="<Colour value>"` on every model
// shot (gallery, thumbnails, modal) whose garment code matches a variant
// SKU (product-media-gallery.liquid / product-media-modal.liquid, see
// their comment), and `roFilterModelColour` + `applyModelColourOnly` below
// hide a stamped shot whose colour differs from the selected Colour. Media
// with no stamp (mocks, unmatched shots) are never touched. Products WITH a
// Printed option, and products with no Colour option, keep their existing
// behaviour exactly.

(function (root) {
  'use strict';

  // --- 1. Pure filter -------------------------------------------------
  //
  // Rule (restated from the build brief, corrected 22 Sep 2026 independent
  // review HIGH #1): hide a slide when its own `colour` is non-empty AND
  // differs (case-insensitively) from the selected colour - BUT ONLY when
  // the product actually has a Colour option AND a value is selected.
  // `colour: null` (or `undefined`) means "no colour filter" - a
  // single-colour product has NO Colour option at all (fulfilment-bridge/
  // lib/imports.js: `multiColour = colours.length > 1`, options only gains
  // 'Colour' when there's more than one), but its mock alts still carry
  // the colour word (e.g. "Black") - so `slide.colour` is non-empty even
  // though there is nothing to filter ON. Passing `colour: ''` (a Colour
  // option EXISTS but nothing is selected yet) is different and DOES
  // filter, hiding every coloured slide - that's the DOM wiring's own
  // `getSelectedOptionValue` returning `null` (no matching elements found
  // at all = no Colour option) vs `''` (elements found, none checked yet).
  // ALSO hide a slide when Printed is exactly "Front Only" and the
  // slide's `side` is "back"; otherwise show it. A slide with an empty
  // `colour` (a model shot, or any image the alt parser found no colour
  // word in) is never hidden by the colour rule - it's shown for every
  // colour - UNLESS its `garmentCode` (26 Sep 2026, see file header) maps
  // to a colour via this product's OWN mock slides, in which case that
  // mapped colour is used exactly as if it were the slide's own `colour`.
  // `printed === null` or `undefined` means "this product has no
  // Printed option" - every slide is returned visible, untouched.
  function roFilterSlides(slides, opts) {
    var options = opts || {};
    var printed = options.printed;
    var list = slides || [];

    if (printed === undefined || printed === null) {
      return list.map(function (slide) {
        return assign({}, slide, { visible: true });
      });
    }

    var hasColourFilter = options.colour !== undefined && options.colour !== null;
    var selectedColour = hasColourFilter ? String(options.colour).trim().toLowerCase() : '';
    // v3 (23 Sep live-preview review, item 3): normalised the same way as
    // selectedColour, used only by the frontVariantKeys branch below -
    // printed is never null/undefined here (the early return above already
    // handled that case), but CAN be '' (a Printed option exists with
    // nothing selected yet).
    var selectedPrinted = String(printed).trim().toLowerCase();
    var selectedFrontKey = selectedColour + '|' + selectedPrinted;

    // Reviewer finding (23 Sep, live-fix review, MEDIUM): the bound-front
    // rule below REPLACES the plain colour rule for a slide with
    // `frontVariantKeys`, so if the selected colour's ONLY bound front is
    // bound to the OTHER Printed value (e.g. M121's mock never got
    // resolved, so every "Front Only"/Black variant is unbound while
    // "Front & Back"/Black is bound to M101's front), the strict frontKey
    // match hides every front for that colour+Printed combo - the
    // selection ends up with NO front image at all, which the brief says
    // must never happen. Fix: only apply the strict bound-front match when
    // at least one front ANYWHERE in this zone is actually bound to the
    // exact selected colour+Printed combo; otherwise every front falls
    // back to the plain colour/side rule for this whole render (other
    // colours are unaffected either way - the plain rule already hides
    // them on the colour mismatch alone).
    var selectedComboHasBoundFront = list.some(function (slide) {
      var frontKeys = (slide && slide.frontVariantKeys) || [];
      return frontKeys.length > 0 && frontKeys.indexOf(selectedFrontKey) !== -1;
    });

    // 26 Sep 2026 (Sohail): garmentCode -> colour, built from THIS
    // product's OWN mock slides only (never a hard-coded garment/colour
    // list - see file header). A mock slide always has both a non-empty
    // `colour` and the garment code its own alt named (e.g. "on Charcoal
    // M115" -> code "m115" maps to "charcoal"); a model shot has a
    // `garmentCode` but an empty `colour`, so it's never itself a source
    // of a map entry, only a consumer of one, below. First mock found for
    // a given code wins (a code is one specific blank+colour, so every
    // mock for it should agree anyway).
    var codeToColour = {};
    list.forEach(function (slide) {
      var c = slide && slide.colour ? String(slide.colour).trim().toLowerCase() : '';
      var code = slide && slide.garmentCode ? String(slide.garmentCode).trim().toLowerCase() : '';
      if (c && code && !codeToColour[code]) codeToColour[code] = c;
    });

    return list.map(function (slide) {
      var side = slide && slide.side ? String(slide.side) : '';
      var slideColour = slide && slide.colour ? String(slide.colour).trim().toLowerCase() : '';
      var garmentCode = slide && slide.garmentCode ? String(slide.garmentCode).trim().toLowerCase() : '';
      // Effective colour for the plain colour/side rule below: the
      // slide's own colour if it has one (a mock, unchanged), else this
      // product's mapped colour for its garment code (a model shot whose
      // garment is known from a mock elsewhere in THIS product), else ''
      // (no mock anywhere in this product names that code - keeps today's
      // behaviour: show for every colour, same as any other unmapped
      // colourless slide). Never used by the bound-front branch below -
      // that branch decides purely from `frontVariantKeys`, and a model
      // shot is never the featured_media of a variant.
      var effectiveColour = slideColour || (garmentCode && codeToColour[garmentCode]) || '';
      var frontKeys = (slide && slide.frontVariantKeys) || [];
      var visible = true;

      if (side === 'front' && frontKeys.length && selectedComboHasBoundFront) {
        // 23 Sep live-preview review, item 3 (duplicate front defect): a
        // front image that IS the featured media of one or more variants
        // is shown ONLY when one of those variants matches BOTH the
        // selected Colour and Printed value - this REPLACES the plain
        // colour rule below for this slide, it does not add to it. Two
        // near-identical front mock-ups can share the same colour word but
        // each be bound to a DIFFERENT Printed variant (Death Raven:
        // "front view on Black M101" is the featured media of the Front &
        // Back/Black variants, "front view on Black M121" is the featured
        // media of the Front Only/Black variants - the plain colour rule
        // alone shows BOTH of them for either Printed choice, since it
        // never looks at Printed at all). A front image with NO variant
        // binding (frontVariantKeys empty - not the featured media of any
        // variant, e.g. a colourless model shot, or a single-photo
        // product) falls through to the "else" branch below, unchanged.
        // Guarded by `selectedComboHasBoundFront` (23 Sep MEDIUM fix, see
        // above): if nothing anywhere is bound to the exact selected
        // colour+Printed combo, this branch is skipped entirely and every
        // front - bound or not - falls back to the plain colour/side rule,
        // so the selected colour never ends up with zero visible fronts.
        visible = frontKeys.indexOf(selectedFrontKey) !== -1;
      } else {
        if (hasColourFilter && effectiveColour && effectiveColour !== selectedColour) visible = false;
        if (visible && printed === 'Front Only' && side === 'back') visible = false;
      }

      return assign({}, slide, { visible: visible });
    });
  }

  // 26 Sep 2026 (Sohail, review round 1, MEDIUM): the garmentCode fallback
  // above can now legitimately hide EVERY slide in a zone - a colour with
  // no visible mock yet (e.g. Acid Wash pending) whose selected variant
  // also has no bound featured media falls through to `roFilterSlides`
  // returning `visible: false` for every slide, including the model shots
  // that used to be the last always-visible fallback before this build.
  // `roFilterSlidesWithFloor` wraps `roFilterSlides` with two floors, tried
  // in order, so a zone is never left fully blank:
  //   1. Recompute WITHOUT the 26 Sep garmentCode rule (strip `garmentCode`
  //      from every slide first) - this is exactly today's/pre-this-build
  //      behaviour: a colourless slide (a model shot) is never hidden by a
  //      colour it merely shares a garment code with, so it re-appears.
  //   2. If STILL nothing is visible (a genuinely empty zone, or every
  //      slide's own `colour` truly conflicts), show every slide rather
  //      than render an empty gallery.
  // Kept as a separate function (not folded into `roFilterSlides` itself)
  // so the normal, non-floor path stays exactly as every existing caller
  // and test expects - this wrapper is what the DOM wiring calls instead.
  function roFilterSlidesWithFloor(slides, opts) {
    var list = slides || [];
    var results = roFilterSlides(list, opts);
    if (anyVisible(results)) return results;

    var withoutGarmentCode = roFilterSlides(stripGarmentCode(list), opts);
    if (anyVisible(withoutGarmentCode)) {
      // Re-apply the recomputed `visible` flags to the ORIGINAL slides so
      // callers never lose `garmentCode` (or any other field) off the
      // result - only the floor's visibility decision is borrowed.
      return list.map(function (slide, i) {
        return assign({}, slide, { visible: withoutGarmentCode[i].visible });
      });
    }

    return list.map(function (slide) {
      return assign({}, slide, { visible: true });
    });
  }

  // 29 Sep 2026: Colour-only pass (see file header). `slides` are
  // {id, modelColour}; `modelColour` is the Liquid-stamped colour of a model
  // shot ('' for anything else). Hide a slide only when it HAS a modelColour,
  // a Colour is actually selected, and the two differ (trimmed,
  // case-insensitive). `colour` null/undefined (no Colour option) or ''
  // (nothing selected yet) hides nothing. Floor: if every slide would be
  // hidden, show them all rather than leave a blank gallery.
  function roFilterModelColour(slides, opts) {
    var list = slides || [];
    var raw = opts ? opts.colour : null;
    var selected = raw === undefined || raw === null ? '' : String(raw).trim().toLowerCase();
    var results = list.map(function (slide) {
      var mc = slide && slide.modelColour ? String(slide.modelColour).trim().toLowerCase() : '';
      var visible = !(selected && mc && mc !== selected);
      return assign({}, slide, { visible: visible });
    });
    if (!anyVisible(results)) {
      return list.map(function (slide) {
        return assign({}, slide, { visible: true });
      });
    }
    return results;
  }

  function anyVisible(results) {
    for (var i = 0; i < results.length; i++) {
      if (results[i] && results[i].visible) return true;
    }
    return false;
  }

  function stripGarmentCode(slides) {
    return (slides || []).map(function (slide) {
      var out = {};
      for (var key in slide) {
        if (Object.prototype.hasOwnProperty.call(slide, key) && key !== 'garmentCode') out[key] = slide[key];
      }
      return out;
    });
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var key in src) {
        if (Object.prototype.hasOwnProperty.call(src, key)) target[key] = src[key];
      }
    }
    return target;
  }

  // Export for `node --test` (CommonJS) and for the browser (global).
  // `computeVisibleSliderCounter` is defined further down (DOM-wiring
  // section, right next to the arrow/counter DOM code it supports) but is
  // itself pure/DOM-free (see its own comment) and is hoisted, so it's
  // safe to reference here - one export block for both pure functions.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      roFilterSlides: roFilterSlides,
      roFilterSlidesWithFloor: roFilterSlidesWithFloor,
      roFilterModelColour: roFilterModelColour,
      computeVisibleSliderCounter: computeVisibleSliderCounter,
    };
  }
  if (root) {
    root.RoMediaFilter = assign(root.RoMediaFilter || {}, {
      roFilterSlides: roFilterSlides,
      roFilterSlidesWithFloor: roFilterSlidesWithFloor,
      roFilterModelColour: roFilterModelColour,
      computeVisibleSliderCounter: computeVisibleSliderCounter,
    });
  }

  // --- 2. DOM wiring (browser only - function DEFINITIONS below are safe
  // to load in Node since they only touch document/window when actually
  // CALLED; only the final auto-run at the bottom of this file is guarded
  // by `hasDom`.) --------------------------------------------------------
  var hasDom = typeof document !== 'undefined';

  // 29 Sep 2026 review: the Colour option may be named "Colour" or "Color"
  // (any case); every other option name still matches exactly (case-
  // insensitive). `wanted` is the logical name the caller asks for.
  function optionNameMatches(actual, wanted) {
    var a = String(actual == null ? '' : actual).trim().toLowerCase();
    var w = String(wanted).trim().toLowerCase();
    if (w === 'colour' || w === 'color') return a === 'colour' || a === 'color';
    return a === w;
  }

  function getSelectedOptionValue(scope, optionName) {
    var els = scope.querySelectorAll('[data-option-name]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!optionNameMatches(el.getAttribute('data-option-name'), optionName)) continue;
      if (el.tagName === 'INPUT' && el.checked) return el.value;
      if (el.tagName === 'OPTION' && el.selected) return el.value;
    }
    return null;
  }

  function hasPrintedOption(productInfoEl) {
    return !!productInfoEl.querySelector('[data-option-name="Printed"]');
  }

  // 23 Sep live-preview review, item 3: `data-ro-front-variant-keys` is a
  // comma-separated list of `<colour>|<printed>` pairs (lowercase, stamped
  // by Liquid from `product.variants` - see product-media-gallery.liquid/
  // product-media-modal.liquid's own comment) for every variant this
  // FRONT media is the featured_media of. Empty/absent for any media that
  // is never a variant's featured_media (model shots, backs, a single
  // representative photo) - those keep the plain colour/side rule.
  function parseFrontVariantKeys(raw) {
    if (!raw) return [];
    return String(raw)
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  // Collects one zone's own `[data-ro-media-id]` elements - the gallery
  // slide `<li>`s, the thumbnail `<li>`s, or the modal's per-media wrapper
  // elements - each zone independently, never derived from another zone.
  function collectZoneSlides(root, selector) {
    if (!root) return [];
    var items = root.querySelectorAll(selector);
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      out.push({
        id: el.getAttribute('data-ro-media-id') || '',
        side: el.getAttribute('data-ro-side') || '',
        colour: el.getAttribute('data-ro-colour') || '',
        garmentCode: el.getAttribute('data-ro-garment-code') || '',
        modelColour: el.getAttribute('data-ro-model-colour') || '',
        frontVariantKeys: parseFrontVariantKeys(el.getAttribute('data-ro-front-variant-keys')),
        el: el,
        mediaId: el.getAttribute('data-media-id') || '',
      });
    }
    return out;
  }

  // Runs the pure filter (with its zero-visible floors, see
  // `roFilterSlidesWithFloor` above) against one zone's own slides and
  // applies visibility to that zone's own elements. Returns the hidden-id
  // set for that zone (used by the gallery zone's own active-slide/counter
  // logic below - never fed INTO another zone's filtering, only read back
  // from it for bookkeeping that belongs to that same zone). Each zone
  // carries the same media set (see file header, v3) and runs this same
  // deterministic floor logic independently, so the three zones agree
  // without needing to share state.
  function applyZoneFilter(zoneSlides, opts) {
    var results = roFilterSlidesWithFloor(
      zoneSlides.map(function (s) {
        return { id: s.id, side: s.side, colour: s.colour, garmentCode: s.garmentCode, frontVariantKeys: s.frontVariantKeys };
      }),
      opts
    );
    var hiddenIds = {};
    results.forEach(function (result, index) {
      var slide = zoneSlides[index];
      setVisible(slide.el, result.visible);
      if (!result.visible) hiddenIds[slide.id] = true;
    });
    return hiddenIds;
  }

  function setVisible(el, visible) {
    if (!el) return;
    el.style.display = visible ? '' : 'none';
    if (visible) el.removeAttribute('data-ro-hidden');
    else el.setAttribute('data-ro-hidden', 'true');
  }

  // 23 Sep live-preview review, item 2 (zoom never filtered): Dawn's own
  // `ModalDialog.connectedCallback` (theme/assets/global.js, not owned by
  // this brief) moves `<product-modal>` to be a direct child of `<body>`
  // the moment it upgrades - measured on the real page, `product-modal`'s
  // `parentElement` is `BODY`, never `productInfoEl`. The old
  // `productInfoEl.querySelector('.product-media-modal__content')` lookup
  // therefore always returned null on the real page (it only ever worked
  // in a fake-DOM test that never modelled the move), so the modal zone
  // was silently skipped - never filtered at all. Dawn's OWN
  // `product-info.js` (`get productModal()`) already looks this element up
  // the same way this mirrors: by id, built from the product-info's own
  // `data-section` value (`#ProductModal-${this.dataset.section}`) -
  // anywhere in the document, not by ancestry. A quick-add product-info's
  // `data-section` is rewritten to `quickadd-<sectionId>` by quick-add.js's
  // `preventDuplicatedIDs` BEFORE its own `<product-modal>` is deleted
  // entirely (`removeDOMElements`) - so this same by-id lookup naturally
  // finds nothing for a quick-add product-info (correct: quick-add has no
  // zoom modal) and can never cross-match the PAGE's own modal, which
  // keeps the un-prefixed id.
  function findProductModalElement(productInfoEl) {
    if (!productInfoEl) return null;
    var sectionId = productInfoEl.getAttribute('data-section');
    if (!sectionId) return null;
    return document.getElementById('ProductModal-' + sectionId) || null;
  }

  // 23 Sep live-preview review, item 2: re-run the filter whenever THIS
  // modal opens, not only on load/variant change - covers a modal found
  // once (e.g. at page load) then reopened later without an intervening
  // variant-change pub/sub event. Dawn's own `ModalDialog.show()` (global.
  // js) only ever does `this.setAttribute('open', '')` - no custom event
  // is dispatched anywhere in this theme - so a MutationObserver on that
  // one attribute is the real, always-fires hook. An `'open'` event
  // listener is ALSO attached, defensively (a harmless no-op today, since
  // nothing in this theme dispatches one), in case a future Dawn update
  // moves to an explicit event instead of the attribute. Guarded so a
  // given modal element is only ever wired up once, however many times
  // applyRoMediaFilter runs for its product-info.
  function watchModalOpen(modalEl, productInfoEl) {
    if (!modalEl || modalEl.__roMediaFilterOpenWatched) return;
    modalEl.__roMediaFilterOpenWatched = true;

    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function () {
        if (modalEl.getAttribute('open') !== null) applyRoMediaFilter(productInfoEl);
      });
      observer.observe(modalEl, { attributes: true, attributeFilter: ['open'] });
    }
    if (typeof modalEl.addEventListener === 'function') {
      modalEl.addEventListener('open', function () {
        applyRoMediaFilter(productInfoEl);
      });
    }
  }

  // 29 Sep 2026 review (stale visibility on unavailable combinations): Dawn's
  // product-info.js returns from handleUpdateProductInfo WITHOUT publishing
  // variantChange when the picked option combination has no variant (e.g.
  // Black / XL missing), so the Colour-only pass would keep the previous
  // colour's model shots. Also re-run it when the Colour option input itself
  // changes, reading the CURRENTLY SELECTED colour from the picker (not from
  // a variant). One delegated `change` listener on the product-info element
  // (the picker's own DOM is replaced on every variant change, so a listener
  // on the inputs would be lost); guarded so an element is wired only once.
  // The Printed path is untouched: this only fires the Colour-only pass, and
  // only on a product with no Printed option. variantChange still runs it too.
  function isColourPickerTarget(target) {
    if (!target || !target.tagName) return false;
    if (target.tagName === 'INPUT') {
      return optionNameMatches(target.getAttribute('data-option-name'), 'Colour');
    }
    if (target.tagName === 'SELECT' || target.tagName === 'OPTION') {
      if (optionNameMatches(target.getAttribute('data-option-name'), 'Colour')) return true;
      var opts = target.querySelectorAll ? target.querySelectorAll('[data-option-name]') : [];
      for (var i = 0; i < opts.length; i++) {
        if (optionNameMatches(opts[i].getAttribute('data-option-name'), 'Colour')) return true;
      }
    }
    return false;
  }

  function watchColourChange(productInfoEl) {
    if (!productInfoEl || productInfoEl.__roMediaFilterColourWatched) return;
    if (typeof productInfoEl.addEventListener !== 'function') return;
    productInfoEl.__roMediaFilterColourWatched = true;
    productInfoEl.addEventListener('change', function (event) {
      if (!event || !isColourPickerTarget(event.target)) return;
      if (hasPrintedOption(productInfoEl)) return;
      applyModelColourOnly(productInfoEl);
    });
  }

  function applyRoMediaFilter(productInfoEl) {
    if (!productInfoEl) return;
    watchColourChange(productInfoEl);
    if (!hasPrintedOption(productInfoEl)) {
      // No Printed option: stock Dawn, except the 29 Sep Colour-only model
      // shot pass (no-op unless Liquid stamped a model shot).
      applyModelColourOnly(productInfoEl);
      return;
    }

    var galleryComponent = productInfoEl.querySelector('media-gallery');
    if (!galleryComponent) return;
    var galleryUl = galleryComponent.querySelector('ul.product__media-list');
    if (!galleryUl) return;

    var colour = getSelectedOptionValue(productInfoEl, 'Colour');
    var printed = getSelectedOptionValue(productInfoEl, 'Printed');
    var opts = { colour: colour, printed: printed };

    // v3: each zone is collected and filtered from ITS OWN elements - the
    // gallery slide list, the thumbnail list, and the modal - never by
    // deriving one zone's hidden set from another. Liquid now renders the
    // same media set into all three zones for a Printed-option product
    // (see product-media-gallery.liquid item 1), so the three zones are
    // expected to agree, but each is filtered independently so a future
    // mismatch fails safe (that zone's own elements, not silently skipped).
    var gallerySlides = collectZoneSlides(galleryUl, 'li[data-ro-media-id]');
    if (!gallerySlides.length) return;
    var galleryHidden = applyZoneFilter(gallerySlides, opts);

    var thumbUl = galleryComponent.querySelector('[id^="Slider-Thumbnails-"]');
    if (thumbUl) {
      applyZoneFilter(collectZoneSlides(thumbUl, 'li[data-ro-media-id]'), opts);
    }

    var modalEl = findProductModalElement(productInfoEl);
    var modalContent = modalEl ? modalEl.querySelector('.product-media-modal__content') : null;
    if (modalContent) {
      applyZoneFilter(collectZoneSlides(modalContent, '[data-ro-media-id]'), opts);
    }
    if (modalEl) watchModalOpen(modalEl, productInfoEl);

    syncGalleryAfterFilter(galleryComponent, galleryUl, gallerySlides, galleryHidden);
  }

  // Active-slide swap + slider counter/arrows for the gallery zone. Shared
  // by the Printed path (`applyRoMediaFilter`) and the Colour-only model
  // shot path (`applyModelColourOnly`); body unchanged from v3.
  function syncGalleryAfterFilter(galleryComponent, galleryUl, gallerySlides, galleryHidden) {
    // Active-slide swap belongs to the gallery zone alone - it reads the
    // gallery's own `is-active` slide and its own hidden set, never the
    // thumbnail or modal zone's.
    var activeLi = galleryUl.querySelector('li.is-active');
    var activeId = activeLi ? activeLi.getAttribute('data-ro-media-id') : null;
    if (activeId && galleryHidden[activeId]) {
      var firstVisible = null;
      for (var i = 0; i < gallerySlides.length; i++) {
        if (!galleryHidden[gallerySlides[i].id]) {
          firstVisible = gallerySlides[i];
          break;
        }
      }
      // If the slide that was active got hidden, jump to the first visible
      // one via media-gallery's own setActiveMedia (prepend: true), which
      // also calls the viewer slider's resetPages() internally.
      if (firstVisible && typeof galleryComponent.setActiveMedia === 'function') {
        galleryComponent.setActiveMedia(firstVisible.mediaId, true);
      }
    }

    // Independent review, 22 Sep 2026, item 3: `SliderComponent.initPages()`
    // (theme/assets/global.js, not owned by this brief) returns early -
    // WITHOUT calling `update()` - when fewer than 2 slides are visible, so
    // calling `resetPages()` alone leaves a stale "1 / N" counter and
    // stale arrow-disabled state; this v2 also dropped the 18 Sep patch's
    // hidden-count subtraction, so the counter would otherwise render from
    // the unfiltered `media_count`. Set the counter/arrows explicitly
    // here, driven by OUR OWN visible count (the gallery zone's own count -
    // the thumbnail zone renders the identical media set so the same
    // number applies to its own slider-component too), rather than
    // depending on Dawn's own `update()` reaching the fewer-than-2 case.
    var visibleCount = gallerySlides.length - Object.keys(galleryHidden).length;
    var viewerSlider = galleryComponent.querySelector('slider-component[id^="GalleryViewer-"]');
    var thumbSlider = galleryComponent.querySelector('slider-component[id^="GalleryThumbnails-"]');
    applySliderCounterAndArrows(viewerSlider, visibleCount, true);
    applySliderCounterAndArrows(thumbSlider, visibleCount, false);
  }

  // 29 Sep 2026 (Sohail): Colour-only products (Colour option, NO Printed
  // option). Same three independent zones as the Printed path, but the only
  // rule is `roFilterModelColour`: a model shot whose Liquid-stamped
  // `data-ro-model-colour` differs from the selected Colour is hidden;
  // everything else (mocks, unstamped shots) is left exactly as stock Dawn
  // has it. Does nothing unless the gallery carries at least one stamp, and
  // only runs the active-slide/counter sync when something is (or just
  // stopped being) hidden, so a product with nothing to hide is untouched.
  function applyModelColourOnly(productInfoEl) {
    var colour = getSelectedOptionValue(productInfoEl, 'Colour');
    if (colour === null) return; // no Colour option at all: nothing to filter on

    var galleryComponent = productInfoEl.querySelector('media-gallery');
    if (!galleryComponent) return;
    var galleryUl = galleryComponent.querySelector('ul.product__media-list');
    if (!galleryUl) return;

    var gallerySlides = collectZoneSlides(galleryUl, 'li[data-ro-media-id]');
    var stamped = gallerySlides.some(function (s) {
      return !!s.modelColour;
    });
    if (!stamped) return;

    var opts = { colour: colour };
    var galleryHidden = applyModelColourZone(gallerySlides, opts);

    var thumbUl = galleryComponent.querySelector('[id^="Slider-Thumbnails-"]');
    if (thumbUl) applyModelColourZone(collectZoneSlides(thumbUl, 'li[data-ro-media-id]'), opts);

    var modalEl = findProductModalElement(productInfoEl);
    var modalContent = modalEl ? modalEl.querySelector('.product-media-modal__content') : null;
    if (modalContent) applyModelColourZone(collectZoneSlides(modalContent, '[data-ro-media-id]'), opts);
    if (modalEl) watchModalOpen(modalEl, productInfoEl);

    var anyHidden = Object.keys(galleryHidden).length > 0;
    if (!anyHidden && !productInfoEl.__roModelHidden) return;
    productInfoEl.__roModelHidden = anyHidden;

    var activeLi = galleryUl.querySelector('li.is-active');
    var activeId = activeLi ? activeLi.getAttribute('data-ro-media-id') : null;
    if (activeId && galleryHidden[activeId]) {
      var firstVisible = null;
      for (var i = 0; i < gallerySlides.length; i++) {
        if (!galleryHidden[gallerySlides[i].id]) {
          firstVisible = gallerySlides[i];
          break;
        }
      }
      if (firstVisible && typeof galleryComponent.setActiveMedia === 'function') {
        galleryComponent.setActiveMedia(firstVisible.mediaId, true);
      }
    }

    var visibleCount = gallerySlides.length - Object.keys(galleryHidden).length;
    var viewerSlider = galleryComponent.querySelector('slider-component[id^="GalleryViewer-"]');
    var thumbSlider = galleryComponent.querySelector('slider-component[id^="GalleryThumbnails-"]');
    applySliderCounterAndArrows(viewerSlider, visibleCount, true);
    // The thumbnail list also holds CSS-hidden variant thumbnails (Dawn
    // hide_variants), so only the fewer-than-2 case is decided from our
    // count; otherwise Dawn's own resetPages() recounts what is rendered.
    if (thumbSlider) {
      if (visibleCount < 2) setArrowsDisabled(thumbSlider, true);
      else if (typeof thumbSlider.resetPages === 'function') thumbSlider.resetPages();
    }
  }

  function applyModelColourZone(zoneSlides, opts) {
    var results = roFilterModelColour(
      zoneSlides.map(function (s) {
        return { id: s.id, modelColour: s.modelColour };
      }),
      opts
    );
    var hiddenIds = {};
    results.forEach(function (result, index) {
      var slide = zoneSlides[index];
      // Only stamped model shots are ever touched; anything else keeps
      // whatever Dawn/CSS gave it.
      if (slide.modelColour) setVisible(slide.el, result.visible);
      if (!result.visible) hiddenIds[slide.id] = true;
    });
    return hiddenIds;
  }

  // Pure arithmetic (unit-testable, no DOM): what the "1 / N" counter
  // should read for a given count of visible slides. Dawn always shows at
  // least "1 / 1", never "1 / 0", even when nothing matched (shouldn't
  // happen in practice - a colourless/sideless slide is always shown - but
  // defend against it rather than rendering "1 / 0").
  function computeVisibleSliderCounter(visibleCount) {
    var count = typeof visibleCount === 'number' && visibleCount > 0 ? visibleCount : 1;
    return { current: 1, total: count };
  }

  function setArrowsDisabled(slider, disabled) {
    if (!slider) return;
    var prev = slider.querySelector('button[name="previous"]');
    var next = slider.querySelector('button[name="next"]');
    [prev, next].forEach(function (btn) {
      if (!btn) return;
      if (disabled) btn.setAttribute('disabled', 'disabled');
      else btn.removeAttribute('disabled');
    });
  }

  // `hasCounter`: the main GalleryViewer slider-component renders a
  // `.slider-counter--current`/`--total` pair; the GalleryThumbnails one
  // (theme/snippets/product-media-gallery.liquid) does not - arrows only.
  function applySliderCounterAndArrows(slider, visibleCount, hasCounter) {
    if (!slider) return;

    if (visibleCount < 2) {
      // Mirrors what Dawn's own `update()` would do for a single (or
      // zero) visible slide, since `initPages()` never reaches `update()`
      // in this case to do it itself.
      setArrowsDisabled(slider, true);
      if (hasCounter) {
        var counter = computeVisibleSliderCounter(visibleCount);
        var currentEl = slider.querySelector('.slider-counter--current');
        var totalEl = slider.querySelector('.slider-counter--total');
        if (currentEl) currentEl.textContent = String(counter.current);
        if (totalEl) totalEl.textContent = String(counter.total);
      }
      return;
    }

    // 2+ visible: Dawn's own `resetPages()` -> `initPages()` -> `update()`
    // chain runs fine here (`sliderItemsToShow` is filtered to
    // `clientWidth > 0`, which a `display: none` slide no longer has) and
    // sets both the counter and the arrow-disabled state - but verify the
    // counter total against OUR OWN visible count afterwards and correct
    // it if `update()` left something stale.
    if (typeof slider.resetPages === 'function') slider.resetPages();
    if (hasCounter) {
      var totalEl2 = slider.querySelector('.slider-counter--total');
      if (totalEl2 && String(totalEl2.textContent).trim() !== String(visibleCount)) {
        totalEl2.textContent = String(visibleCount);
      }
    }
  }

  function applyToAll() {
    var all = document.querySelectorAll('product-info');
    for (var i = 0; i < all.length; i++) applyRoMediaFilter(all[i]);
  }

  // Independent review, 22 Sep 2026, item 4: this script is emitted from
  // INSIDE theme/snippets/product-media-gallery.liquid's own <script> tag,
  // which is rendered - and so starts loading - before main-product.liquid
  // gets to `media-gallery.js` (defines the `<media-gallery>` custom
  // element, including `setActiveMedia`). Running the first pass straight
  // away risked calling `galleryComponent.setActiveMedia` before it was a
  // function. Wait for the two custom elements this file actually calls
  // methods on to be upgraded first.
  function runFilterPass() {
    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      Promise.all([customElements.whenDefined('media-gallery'), customElements.whenDefined('slider-component')]).then(
        applyToAll,
        applyToAll // still run even if whenDefined rejects/never resolves on some odd page
      );
    } else {
      applyToAll();
    }
  }

  function setupGlobalListenersOnce() {
    document.addEventListener('product-info:loaded', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('product-info') : null;
      applyRoMediaFilter(target);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var sectionId = event && event.data && event.data.sectionId;
        var productInfoEl = sectionId ? document.getElementById('MainProduct-' + sectionId) : null;
        if (productInfoEl) {
          applyRoMediaFilter(productInfoEl);
        } else {
          applyToAll();
        }
      });
    }
  }

  function init() {
    // Run a filter pass every time this script executes (including a
    // quick-add re-injection - item 6 below) - custom-element upgrade is
    // waited for first (item 4), and a DOMContentLoaded retry covers a
    // pass that ran before layout (`clientWidth` reads inside Dawn's own
    // `resetPages()`) had settled.
    runFilterPass();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runFilterPass);
    }

    // Independent review, 22 Sep 2026, item 6: quick-add re-injects THIS
    // EXACT <script> tag (product-media-gallery.liquid is re-rendered
    // fresh into the quick-add modal) every time its modal opens - without
    // this guard, `setupGlobalListenersOnce()` would `subscribe()` to
    // `PUB_SUB_EVENTS.variantChange` again on every open, stacking up one
    // more duplicate filter pass per variant change for the life of the
    // page. A window-level flag makes the ONE-TIME global listeners
    // attach exactly once, while `runFilterPass()` above still runs fresh
    // every time to catch the newly injected quick-add gallery.
    if (root && !root.__roMediaFilterListenersAttached) {
      root.__roMediaFilterListenersAttached = true;
      setupGlobalListenersOnce();
    }
  }

  if (hasDom) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})(typeof window !== 'undefined' ? window : this);
